import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { useParams } from 'react-router'
import { Button, Icon, Segment } from 'semantic-ui-react'
import { ContentHeader } from '../../../../components/Header/ContentHeader'
import { useWindowSize } from '../../../../hooks/useWindowSize'
import { RootState } from '../../../../store/rootState'
import { IRouteParams } from '../../../../types/routeParams'
import { IUserDetail } from '../../../../types/user'
import { abbreviateString } from '../../../../utils/abbreviateString'
import { checkAdminStatus, checkRole } from '../../../../utils/checkRole'
import { currencyFormat } from '../../../../utils/currencyFormat'
import * as ActivityActions from '../../actions/activitiesActions'
import * as ProjectActions from '../../actions/projectActions'
import BudgetModalComponent from '../../components/budget/BudgetModalComponent'
import { getAllActSelector } from '../../selectors/activitiesSelector'
import { getBudgetCostsSelector } from '../../selectors/budgetCostsSelector'
import { IProjectBudgetCost } from '../../types/costs'
import { IProject, IProjectBudgetChanges, IProjectBudgetChangesBulk } from '../../types/project'
import { IProjectActivity } from '../../types/projectActivity'
import ServiceProviderBudgetHeader from './service-provider/ServiceProviderBudgetListHeaderContainer'


interface StateProps {
  project: IProject
  user: IUserDetail | null
  budgetChanges: IProjectBudgetChanges[]
  activities: IProjectActivity[]
  budgetCosts: IProjectBudgetCost[]
}

interface DispatchProps {
  getActFilters: (id: string | number) => void
  getBudgetChanges: (projectId: string | number) => void
  updateBudgetChanges: (projectId: string | number, budget: IProjectBudgetChangesBulk) => Promise<IProjectBudgetChanges[]>
  getProject: (id: string | number) => void
}

type Props = StateProps & DispatchProps

const ProjectBudgetHeader = (props: Props): JSX.Element => {

  const {
    project,
    user,
    budgetChanges,
    activities,
    budgetCosts,
    getActFilters,
    getBudgetChanges,
    updateBudgetChanges,
    getProject,
  } = props

  const param: IRouteParams = useParams() as IRouteParams
  const [budgetsState, setBudgetsState] = useState(budgetChanges)
  const [openModal, setOpenModal] = useState(false)

  const _size = useWindowSize()

  useEffect(() => {
    if (project.type && project.type !== 'service_provider') {
      getActFilters(param.id)
      getBudgetChanges(param.id)
    }
  }, [getActFilters, getBudgetChanges, param.id, project])

  useEffect(() => {
    setBudgetsState(budgetChanges)
  }, [budgetChanges])

  const calcTotal = (acts: IProjectActivity[], key: 'forecast'): number => {
    const numberList = acts.map(a => a[key] ?? 0).concat(budgetCosts.map(a => +a.total_cost))
    const canceledCosts = budgetCosts.filter(a => a.position_status === 'closed').map(a => +a.total_cost - +a.thereof_paid)
    return numberList.reduce((a, b) => a + b, 0) - canceledCosts.reduce((a, b) => a + b, 0)
  }

  const addBudget = (): void => {
    setOpenModal(true)
  }

  const onClose = (): void => {
    getBudgetChanges(param.id)
    setOpenModal(false)
  }

  const renderExpAndForContent = (modal: boolean): JSX.Element => {
    return (
      <div style={{ display: 'flex', justifyContent: 'space-between', margin: modal ? '0 .5rem' : '2rem 0', flexDirection: modal ? 'column' : 'row' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'baseline', flexDirection: modal ? 'column' : 'row' }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Icon name='circle' size='small' style={{ color: '#4185FF' }} />
              <p style={{ color: '#4185FF', fontSize: '.86rem' }}>Forecast</p>
            </div>
            <div style={{ display: 'flex', float: 'left', color: '#172533', fontWeight: 'bold', marginLeft: modal ? 0 : '.5rem' }}>
              <p style={{ margin: 0 }}>{currencyFormat(calcTotal(activities, 'forecast'))[0]} {currencyFormat(calcTotal(activities, 'forecast')).split(',')[0].replace(currencyFormat(calcTotal(activities, 'forecast'))[0], '')}</p>
              <p style={{ color: '#BEC8C3' }}>,{currencyFormat(calcTotal(activities, 'forecast')).split(',')[1]}</p>
            </div>
          </div>
        </div>
        <div>
          <div style={{ display: 'flex', alignItems: 'baseline', flexDirection: modal ? 'column' : 'row' }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Icon name='circle' size='small' style={{ color: '#FA5953' }} />
              <p style={{ color: '#FA5953', fontSize: '.86rem' }}>Expenditures</p>
            </div>
            <div style={{ display: 'flex', float: 'left', color: '#172533', fontWeight: 'bold', marginLeft: modal ? 0 : '.5rem' }}>
              <p style={{ margin: 0 }}>{currencyFormat(project.expenditures_budget_widget)[0]} {currencyFormat(project.expenditures_budget_widget).split(',')[0].replace(currencyFormat(project.expenditures_budget_widget)[0], '')}</p>
              <p style={{ color: '#BEC8C3' }}>,{currencyFormat(project.expenditures_budget_widget).split(',')[1]}</p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const renderAddBudget = (): JSX.Element | null => {
    if (user && checkRole(user, [18])) {
      return (
        <Button
          className='button-add-item'
          inverted
          size='small'
          color='teal'
          onClick={() => addBudget()}
          content='Add Budget'
          icon={<Icon name='plus' />}
          style={{ height: '2.2rem', padding: '0rem', color: '#08D6A1', marginTop: '.5rem', borderRadius: '.34rem', width: '7.7rem', backgroundColor: 'transparent' }}
        />
      )
    }
    return null
  }

  const budgetWidget = (): JSX.Element => {
    if (project.type !== 'internal' && user && (checkRole(user, [16]) || checkAdminStatus(user, project))) {
      return (
        <Segment style={{ margin: 0, border: 'none', borderRadius: '.57rem', boxShadow: '0 0 25px -5px #15151511', width: 'auto', minWidth: '25rem', height: '8.2rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', flexDirection: 'column', textAlign: 'left' }}>
              <p style={{ color: '#172533', fontWeight: 'bold', margin: 0 }}>Available Budget</p>
              <div style={{ display: 'flex', float: 'left', color: '#08D6A1', fontWeight: 'bold', fontSize: '1.7rem' }}>
                <p style={{ margin: 0 }}>{currencyFormat(project.financial_calculation.total_budget ? +project.financial_calculation.total_budget : 0)[0]} {currencyFormat(project.financial_calculation.total_budget ? +project.financial_calculation.total_budget : 0).split(',')[0].replace(currencyFormat(project.financial_calculation.total_budget ? +project.financial_calculation.total_budget : 0)[0], '')}</p>
                <p style={{ color: '#BEC8C3' }}>,{currencyFormat(project.financial_calculation.total_budget ? +project.financial_calculation.total_budget : 0).split(',')[1]}</p>
              </div>
            </div>
            <div>
              {renderAddBudget()}
            </div>
          </div>
          <hr style={{ width: '24.9rem', margin: '.5rem -1rem -1.5rem', opacity: .1 }} />
          {renderExpAndForContent(false)}
        </Segment>
      )
    }
    return <div style={{ height: '8.2rem' }}></div>
  }

  const renderContainer = (): JSX.Element => {
    if (project.type && project.type !== 'service_provider') {
      return (
        <React.Fragment>
          <ContentHeader
            id='projectActivitiesHeader'
            title={_size.width > 1280 ? project.name :
              abbreviateString(project.name, _size.width > 1154 ? 15 : 12, true, _size.width > 1154 ? 17 : 14)}
            color='#08D6A1'
            backLocation='/projects'
            backTitle={project.type === 'internal' ? 'Expenditures' : 'Budget'}
            subtitle={_size.width > 1154 ? project.customer?.name ?? project.category :
              abbreviateString(project.customer?.name ?? project.category ?? '', 15, true, 17)}
            rightItemsFirst={budgetWidget()}
            column={true}
          />
          {budgetsState.length !== 0 ?
            <BudgetModalComponent
              project_id={+project.id}
              project_budget={project.financial_calculation.total_budget ? +project.financial_calculation.total_budget : 0}
              budgets={budgetsState}
              openModal={openModal}
              updateBudgetChanges={updateBudgetChanges}
              renderExpAndForContent={renderExpAndForContent}
              onClose={onClose}
              getProject={getProject}
            /> : null}
        </React.Fragment>
      )
    } else {
      return <ServiceProviderBudgetHeader />
    }
  }

  return renderContainer()
}

const mapStateToProps = (state: RootState): StateProps => {
  return {
    project: state.projectsState.project,
    user: state.authState.user,
    budgetChanges: state.projectsState.projectBudgetChanges,
    activities: getAllActSelector(state),
    budgetCosts: getBudgetCostsSelector(state),
  }
}

const mapDispatchToProps = (dispatch: ActivityActions.ActivitiesThunkDispatchType & ProjectActions.ProjectThunkDispatchType): DispatchProps => {
  return {
    getActFilters: id => dispatch(ActivityActions.getActFilters(id)),
    getBudgetChanges: projectId => dispatch(ProjectActions.getBudgetChanges(projectId)),
    updateBudgetChanges: (projectId, budgets) => dispatch(ProjectActions.updateBudgetChanges(projectId, budgets)),
    getProject: (id) => dispatch(ProjectActions.getProject(id)),
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(ProjectBudgetHeader)
